import { Box, Typography, Paper, Chip, LinearProgress, Table, TableBody, TableCell, TableHead, TableRow, Grid } from '@mui/material';
import { Link } from 'react-router-dom';
import { School, TrendingUp, Stars } from '@mui/icons-material';
import type { TrainingProviderRecord } from '@/types/entities';

interface CourseTableItem {
  id: string;
  courseTitle: string;
  nqfLevel: number;
  creditValue: number;
  durationHours: number;
  durationDays?: number;
  deliveryMode: string;
  status: string;
}

export const TrainingProviderCoursesTable = ({ providerCourses, providerCoursesTotal }: { providerCourses: CourseTableItem[]; providerCoursesTotal: number }) => (
  <Grid size={{ xs: 12, md: 12 }}>
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <School color="primary" />
        <Typography variant="subtitle1" fontWeight={700}>Courses ({providerCoursesTotal ?? 0})</Typography>
      </Box>
      {providerCourses && providerCourses.length > 0 ? (
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell><Typography variant="caption" fontWeight={700}>Course Title</Typography></TableCell>
              <TableCell><Typography variant="caption" fontWeight={700}>NQF Level</Typography></TableCell>
              <TableCell><Typography variant="caption" fontWeight={700}>Credits</Typography></TableCell>
              <TableCell><Typography variant="caption" fontWeight={700}>Duration</Typography></TableCell>
              <TableCell><Typography variant="caption" fontWeight={700}>Delivery Mode</Typography></TableCell>
              <TableCell><Typography variant="caption" fontWeight={700}>Status</Typography></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {providerCourses.map((course: CourseTableItem) => (
              <TableRow key={course.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                <TableCell>
                  <Typography
                    variant="body2"
                    component={Link}
                    to={`/courses/${course.id}/show`}
                    sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, cursor: 'pointer' }}
                  >
                    {course.courseTitle}
                  </Typography>
                </TableCell>
                <TableCell><Typography variant="body2">Level {course.nqfLevel}</Typography></TableCell>
                <TableCell><Typography variant="body2">{course.creditValue}</Typography></TableCell>
                <TableCell><Typography variant="body2">{course.durationHours}h {course.durationDays ? `(${course.durationDays}d)` : ''}</Typography></TableCell>
                <TableCell><Chip label={course.deliveryMode} size="small" /></TableCell>
                <TableCell><Chip label={course.status} size="small" color={course.status === 'Active' ? 'success' : course.status === 'Archived' ? 'default' : 'warning'} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography variant="body2" color="text.secondary">No courses registered under this provider</Typography>
      )}
    </Paper>
  </Grid>
);

export const TrainingProviderMetrics = ({ record }: { record: TrainingProviderRecord }) => {
  const completionRate = record.performanceMetrics?.averageCompletionRate || 0;
  const satisfactionScore = record.performanceMetrics?.averageSatisfactionScore || 0;

  return (
    <Grid size={{ xs: 12, md: 4 }}>
      <Paper sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <TrendingUp color="primary" />
          <Typography variant="subtitle1" fontWeight={700}>Performance Metrics</Typography>
        </Box>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell><Typography variant="caption" color="text.secondary">Courses Delivered</Typography></TableCell>
              <TableCell><Typography variant="body1" fontWeight={700}>{record.performanceMetrics?.coursesDelivered ?? 0}</Typography></TableCell>
            </TableRow>
            <TableRow>
              <TableCell><Typography variant="caption" color="text.secondary">Total Learners</Typography></TableCell>
              <TableCell><Typography variant="body1" fontWeight={700}>{record.performanceMetrics?.totalLearners ?? 0}</Typography></TableCell>
            </TableRow>
            <TableRow>
              <TableCell><Typography variant="caption" color="text.secondary">Completion Rate</Typography></TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LinearProgress variant="determinate" value={(completionRate || 0) * 100} sx={{ flex: 1, height: 6, borderRadius: 1 }} />
                  <Typography variant="body2" fontWeight={500}>{Math.round((completionRate || 0) * 100)}%</Typography>
                </Box>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell><Typography variant="caption" color="text.secondary">Satisfaction</Typography></TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Stars fontSize="small" color="warning" />
                  <Typography variant="body1" fontWeight={700}>{satisfactionScore.toFixed(1)} / 5.0</Typography>
                </Box>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>
    </Grid>
  );
};

export const TrainingProviderQuickActions = ({ record }: { record: TrainingProviderRecord }) => (
  <Grid size={{ xs: 12, md: 4 }}>
    <Paper sx={{ p: 3, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <School color="primary" />
        <Typography variant="subtitle1" fontWeight={700}>Quick Actions</Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Chip
          component={Link}
          to={`/courses?provider=${encodeURIComponent(record.providerName)}`}
          clickable
          label="View Courses"
          icon={<TrendingUp />}
          sx={{ justifyContent: 'flex-start' }}
        />
        <Chip
          component={Link}
          to={`/courses/create?providerId=${encodeURIComponent(record.id)}&providerName=${encodeURIComponent(record.providerName)}`}
          clickable
          label="Add Course"
          icon={<School />}
          sx={{ justifyContent: 'flex-start' }}
        />
        <Chip
          component={Link}
          to="/training"
          clickable
          label="View Training Records"
          icon={<School />}
          sx={{ justifyContent: 'flex-start' }}
        />
      </Box>
    </Paper>
  </Grid>
);
